<template>
  <v-main>
    <v-container fluid>
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              title="OTP Logs"
              subtitle="View all OTP requests — latest first"
            >
            </page-header>

            <v-card-text class="px-4 pb-3">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search by phone or email"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    hide-details
                    @click:clear="filter.search_keyword = null; page = 1; getData()"
                    @keydown.enter="page = 1; getData()"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-table</v-icon>
                    <span class="text-body-1 font-weight-medium">OTP Logs List</span>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0 text-center">
                  <v-chip color="primary" outlined x-small>
                    <v-icon left x-small>mdi-cellphone-key</v-icon>
                    {{ count }} {{ count === 1 ? 'Log' : 'Logs' }}
                  </v-chip>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Show:</span>
                    <v-btn-toggle v-model="itemsPerPageIndex" mandatory dense>
                      <v-btn x-small value="10">10</v-btn>
                      <v-btn x-small value="25">25</v-btn>
                      <v-btn x-small value="50">50</v-btn>
                    </v-btn-toggle>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <div v-if="loading" class="text-center py-8">
                <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
                <p class="mt-3 grey--text">Loading OTP logs...</p>
              </div>

              <v-data-table
                v-else
                :headers="headers"
                :items="logs"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="otp-table"
              >
                <!-- # -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Phone -->
                <template #[`item.phone`]="{ item }">
                  <div class="d-flex align-center">
                    <v-icon size="16" color="grey" class="mr-2">mdi-phone</v-icon>
                    <span class="text-body-2">{{ item.phone || '—' }}</span>
                  </div>
                </template>

                <!-- Email -->
                <template #[`item.email`]="{ item }">
                  <div class="d-flex align-center">
                    <v-icon size="16" color="grey" class="mr-2">mdi-email-outline</v-icon>
                    <span class="text-body-2">{{ item.email || '—' }}</span>
                  </div>
                </template>

                <!-- OTP -->
                <template #[`item.otp`]="{ item }">
                  <v-chip small color="orange" outlined class="font-weight-bold">
                    <v-icon left x-small>mdi-shield-key-outline</v-icon>
                    {{ item.otp }}
                  </v-chip>
                </template>

                <!-- Created At -->
                <template #[`item.created_at`]="{ item }">
                  <div class="text-caption">
                    <div class="font-weight-medium">{{ formatDate(item.created_at) }}</div>
                    <div class="grey--text">{{ formatTime(item.created_at) }}</div>
                  </div>
                </template>

                <!-- Updated At -->
                <template #[`item.updated_at`]="{ item }">
                  <div class="text-caption">
                    <div class="font-weight-medium">{{ formatDate(item.updated_at) }}</div>
                    <div class="grey--text">{{ formatTime(item.updated_at) }}</div>
                  </div>
                </template>

                <!-- Empty state -->
                <template #no-data>
                  <div class="text-center py-10">
                    <v-icon size="60" color="grey lighten-3">mdi-cellphone-off</v-icon>
                    <h3 class="text-h6 grey--text mt-3 mb-2">No OTP Logs Found</h3>
                    <p class="grey--text">No logs match your search criteria.</p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Pagination -->
            <v-divider v-if="count > 0"></v-divider>
            <v-card-actions v-if="count > 0" class="py-2 px-4">
              <v-row align="center">
                <v-col cols="12" md="4">
                  <span class="text-caption grey--text">
                    {{ (page - 1) * itemsPerPage + 1 }}–{{ Math.min(page * itemsPerPage, count) }} of {{ count }}
                  </span>
                </v-col>
                <v-col cols="12" md="4" class="text-center">
                  <v-pagination
                    v-model="page"
                    :length="paginationLength"
                    :total-visible="5"
                    circle
                    color="primary"
                  ></v-pagination>
                </v-col>
                <v-col cols="12" md="4" class="text-right">
                  <div class="d-inline-flex align-center">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      dense
                      outlined
                      hide-details
                      style="max-width:65px"
                      @keydown.enter="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "OtpLogsIndex",
  data() {
    return {
      logs: [],
      count: 0,
      page: 1,
      itemsPerPage: 10,
      itemsPerPageIndex: "10",
      paginationLength: 0,
      jumpToPage: 1,
      loading: false,
      filter: { search_keyword: "" },
      headers: [
        { text: "#",          value: "sr_no",      sortable: false, width: "50px",  align: "center" },
        { text: "Phone",      value: "phone",      sortable: false, width: "160px" },
        { text: "Email",      value: "email",      sortable: false, width: "220px" },
        { text: "OTP",        value: "otp",        sortable: false, width: "110px", align: "center" },
        { text: "Created At", value: "created_at", sortable: false, width: "140px" },
        { text: "Updated At", value: "updated_at", sortable: false, width: "140px" },
      ],
    };
  },
  watch: {
    page() { this.jumpToPage = this.page; this.getData(); },
    itemsPerPageIndex(val) { this.itemsPerPage = parseInt(val); this.page = 1; this.getData(); },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.loading = true;
      try {
        let query = `/otp_logs?page=${this.page}&rowsPerPage=${this.itemsPerPage}`;
        if (this.filter.search_keyword) query += `&search_keyword=${this.filter.search_keyword}`;
        const res = await axios.get(query);
        this.logs  = res.data.data;
        this.count = res.data.count;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    goToPage() {
      const p = parseInt(this.jumpToPage);
      if (p >= 1 && p <= this.paginationLength) this.page = p;
    },
    formatDate(dt) {
      if (!dt) return "—";
      return new Date(dt).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
    },
    formatTime(dt) {
      if (!dt) return "";
      return new Date(dt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    },
  },
};
</script>

<style scoped>
.otp-table >>> .v-data-table__wrapper table thead tr th {
  background: #f5f7fa;
  font-size: 0.75rem;
  font-weight: 600;
  color: #455a64;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.otp-table >>> .v-data-table__wrapper table tbody tr:hover {
  background: #f8f9fe !important;
}
</style>
